<template>
  <div class="notification-container">
    <transition
      name="notification"
      @after-leave="handleAfterLeave"
    >
      <div v-show="isVisible"
           class="w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden"
           :class="{
             'bg-green-50 dark:bg-green-900/20 ring-green-200 dark:ring-green-800': type === 'success',
             'bg-red-50 dark:bg-red-900/20 ring-red-200 dark:ring-red-800': type === 'error',
             'bg-blue-50 dark:bg-blue-900/20 ring-blue-200 dark:ring-blue-800': type === 'info',
             'bg-yellow-50 dark:bg-yellow-900/20 ring-yellow-200 dark:ring-yellow-800': type === 'warning'
           }">
          <div class="flex items-start p-4">
            <div class="flex-shrink-0">
              <!-- Success Icon -->
              <svg v-if="type === 'success'" class="h-6 w-6 text-green-400 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <!-- Error Icon -->
              <svg v-else-if="type === 'error'" class="h-6 w-6 text-red-400 dark:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <!-- Info Icon -->
              <svg v-else-if="type === 'info'" class="h-6 w-6 text-blue-400 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <!-- Warning Icon -->
              <svg v-else-if="type === 'warning'" class="h-6 w-6 text-yellow-400 dark:text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div class="ml-3 w-0 flex-1">
              <p class="text-sm font-medium"
                 :class="{
                   'text-green-800 dark:text-green-200': type === 'success',
                   'text-red-800 dark:text-red-200': type === 'error',
                   'text-blue-800 dark:text-blue-200': type === 'info',
                   'text-yellow-800 dark:text-yellow-200': type === 'warning'
                 }">
                {{ message }}
              </p>
            </div>
            <div class="ml-4 flex-shrink-0 flex">
              <button
                @click="close"
                class="inline-flex text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-primary-light rounded-md"
              >
                <span class="sr-only">Close</span>
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref, watch, onBeforeUnmount } from 'vue'

export default defineComponent({
  name: 'ToastNotification',
  props: {
    type: {
      type: String,
      default: 'info',
      validator: (value) => ['success', 'error', 'info', 'warning'].includes(value)
    },
    message: {
      type: String,
      required: true
    },
    duration: {
      type: Number,
      default: 2000
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const isVisible = ref(props.visible)
    let timeout = null

    const close = () => {
      isVisible.value = false
    }

    const handleAfterLeave = () => {
      emit('close')
    }

    watch(() => props.visible, (newValue) => {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      
      isVisible.value = newValue
      
      if (newValue && props.duration > 0) {
        timeout = setTimeout(close, props.duration)
      }
    })

    onBeforeUnmount(() => {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
    })

    return {
      isVisible,
      close,
      handleAfterLeave
    }
  }
})
</script>

<style scoped>
.notification-container {
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 50;
  width: 100%;
  max-width: 24rem;
  padding: 0 1rem;
}

.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.notification-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style> 